import { BellOff, CheckCheck } from 'lucide-react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  EmptyState,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Skeletons,
  Text,
  VStack,
  useDisclosure
} from 'pixelize-design-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useGetUserNotificationsQuery,
  useMarkAllUserNotificationsReadMutation,
  useMarkUserNotificationReadMutation
} from '@core/store/Notifications/NotificationsService';
import type { NotificationRow } from '@core/store/Notifications/notificationTypes';
import { mutedText, ruleBorder } from '@core/Theme/tokens';
import { useAppTheme } from '@core/Theme/useAppTheme';
import { CountTabs } from '@modules/shared/components/ui';

import { NotificationBell } from './NotificationBell';
import { UserFeedRow } from './components/UserFeedRow';
import { groupByBucket, isNeedsYouRow, type NotificationBucket } from './notificationDisplay';

const POLL_MS = 30_000;
const MAX_ROWS = 8;

type PopoverTab = 'needsYou' | 'activity';

export function NotificationCenter() {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tab, setTab] = useState<PopoverTab>('needsYou');

  const { data, isLoading, isError, refetch } = useGetUserNotificationsQuery(undefined, {
    pollingInterval: POLL_MS,
    skipPollingIfUnfocused: true
  });
  const [markRead] = useMarkUserNotificationReadMutation();
  const [markAllRead, markAllState] = useMarkAllUserNotificationsReadMutation();

  const items = data?.items ?? [];
  const unread = data?.counts.unread ?? 0;
  const needsYou = data?.counts.needsYou ?? 0;
  const activityUnread = Math.max(unread - needsYou, 0);

  const activeRows = (
    tab === 'needsYou' ? items.filter(isNeedsYouRow) : items.filter((row) => !isNeedsYouRow(row))
  ).slice(0, MAX_ROWS);
  const groups = groupByBucket(activeRows);

  const bucketLabel: Record<NotificationBucket, string> = {
    today: t('common.today'),
    yesterday: t('common.yesterday'),
    earlier: t('common.earlier')
  };

  const handleOpen = () => {
    onOpen();
    void refetch();
  };

  const handleRowClick = (row: NotificationRow) => {
    if (!row.is_read) void markRead({ id: row.id });
    if (row.link) navigate(row.link);
    onClose();
  };

  const handleOpenInbox = () => {
    navigate('/notifications');
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement='bottom-end' isLazy>
      <PopoverTrigger>
        <NotificationBell needsYou={needsYou} unread={unread} />
      </PopoverTrigger>
      <PopoverContent
        w={{ base: '340px', md: '400px' }}
        maxW='calc(100vw - 24px)'
        p={0}
        overflow='hidden'
        data-testid='notification-center-panel'
      >
        <Flex px={4} pt={3} pb={1} align='center' justify='space-between' gap={2}>
          <Text as='h2' fontWeight={600} fontSize='md'>
            {t('nav.notifications')}
          </Text>
          {unread > 0 ? (
            <Button
              size='sm'
              variant='ghost'
              leftIcon={<CheckCheck size={14} aria-hidden />}
              isLoading={markAllState.isLoading}
              onClick={() => void markAllRead()}
              data-testid='notification-center-mark-all'
            >
              {t('common.markAllRead')}
            </Button>
          ) : null}
        </Flex>

        <Box px={4} borderBottomWidth='1px' borderColor={ruleBorder(colors)} overflowX='auto'>
          <CountTabs<PopoverTab>
            value={tab}
            onChange={setTab}
            aria-label={t('notifUi.filterAria')}
            options={[
              { value: 'needsYou', label: t('common.needsYou'), count: needsYou },
              { value: 'activity', label: t('common.activity'), count: activityUnread }
            ]}
          />
        </Box>

        <PopoverBody px={0} py={0} maxH='460px' overflowY='auto'>
          {isLoading ? (
            <VStack align='stretch' spacing={0}>
              {[0, 1, 2].map((i) => (
                <Box key={i} px={4} py={3}>
                  <Skeletons type='text' noOfLines={2} spacing='8px' />
                </Box>
              ))}
            </VStack>
          ) : isError ? (
            <Box p={4}>
              <Alert
                status='error'
                borderRadius='md'
                fontSize='sm'
                data-testid='notification-center-error'
              >
                <AlertIcon />
                <Text flex={1}>{t('notifUi.loadError')}</Text>
                <Button size='sm' variant='outline' onClick={() => void refetch()}>
                  {t('notifUi.retry')}
                </Button>
              </Alert>
            </Box>
          ) : groups.length === 0 ? (
            <Box py={6} data-testid='notification-center-empty'>
              <EmptyState
                title={tab === 'needsYou' ? t('common.allCaughtUp') : t('common.noNotifications')}
                icon={tab === 'needsYou' ? CheckCheck : BellOff}
                variant='subtle'
                size='sm'
              />
            </Box>
          ) : (
            <VStack align='stretch' spacing={0}>
              {groups.map((group) => (
                <Box key={group.bucket}>
                  <Box
                    px={4}
                    py={1}
                    bg={colors.backgroundColor.secondary}
                    position='sticky'
                    top={0}
                    zIndex={1}
                  >
                    <Text fontSize='xs' fontWeight={600} color={mutedText(colors)}>
                      {bucketLabel[group.bucket]}
                    </Text>
                  </Box>
                  {group.rows.map((row) => (
                    <UserFeedRow
                      key={row.id}
                      compact
                      row={row}
                      testId={`notification-center-${row.id}`}
                      onOpen={() => handleRowClick(row)}
                    />
                  ))}
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>

        <PopoverFooter borderTopWidth='1px' borderColor={ruleBorder(colors)} px={4} py={2}>
          <Button
            size='sm'
            variant='link'
            colorScheme='primary'
            onClick={handleOpenInbox}
            data-testid='notification-center-open-inbox'
          >
            {t('common.openInbox')}
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationCenter;
